import { useState, useRef, useEffect, useCallback } from 'react';
import { useSelected, useFocused, ReactEditor, useSlateStatic } from 'slate-react';
import { Transforms } from 'slate';
import styles from './Embed.module.scss';
import { openImagePreview } from './imagePreview';
import { isDocId, fetchImageUrl } from '../../../PlateEditor/services/imageUploadService';

const HANDLE_POSITIONS = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

const Image = ({ attributes, element, children }) => {
    const { url, alt } = element;
    const editor = useSlateStatic();
    const selected = useSelected();
    const focused = useFocused();
    const containerRef = useRef(null);
    const resizeState = useRef(null);

    const needsFetch = isDocId(url);
    const [resolvedUrl, setResolvedUrl] = useState(() => (needsFetch ? null : url));
    const [loading, setLoading] = useState(needsFetch);

    useEffect(() => {
        if (!url) return;
        if (isDocId(url)) {
            setLoading(true);
            setResolvedUrl(null);
            fetchImageUrl(String(url))
                .then((presignedUrl) => {
                    setResolvedUrl(presignedUrl);
                    setLoading(false);
                })
                .catch((err) => {
                    console.error('Failed to fetch image:', err);
                    setLoading(false);
                });
        } else {
            setResolvedUrl(url);
            setLoading(false);
        }
    }, [url]);

    // Cleanup resize listeners on unmount
    const cleanupRef = useRef(null);
    useEffect(() => {
        return () => {
            if (cleanupRef.current) cleanupRef.current();
        };
    }, []);

    const handleResizeStart = useCallback(
        (direction, e) => {
            e.preventDefault();
            e.stopPropagation();

            const container = containerRef.current;
            if (!container) return;

            const startX = e.clientX;
            const startY = e.clientY;
            const startWidth = container.offsetWidth;
            const startHeight = container.offsetHeight;
            const aspectRatio = startWidth / startHeight;

            resizeState.current = { startX, startY, startWidth, startHeight, aspectRatio, direction };

            const handleMouseMove = (moveEvent) => {
                if (!resizeState.current || !container) return;
                const { startX, startY, startWidth, aspectRatio, direction } = resizeState.current;
                const diffX = moveEvent.clientX - startX;
                const diffY = moveEvent.clientY - startY;

                let newWidth = startWidth;
                const isCorner = direction.length === 2;

                if (isCorner) {
                    const signX = direction.includes('w') ? -1 : 1;
                    const signY = direction.includes('n') ? -1 : 1;
                    const scaledDiffX = diffX * signX;
                    const scaledDiffY = diffY * signY * aspectRatio;
                    const delta = Math.abs(scaledDiffX) > Math.abs(scaledDiffY) ? scaledDiffX : scaledDiffY;
                    newWidth = Math.max(50, startWidth + delta);
                } else if (direction === 'e') {
                    newWidth = Math.max(50, startWidth + diffX);
                } else if (direction === 'w') {
                    newWidth = Math.max(50, startWidth - diffX);
                } else if (direction === 's') {
                    newWidth = Math.max(50, startWidth + diffY * aspectRatio);
                } else if (direction === 'n') {
                    newWidth = Math.max(50, startWidth - diffY * aspectRatio);
                }

                container.style.width = `${Math.round(newWidth)}px`;
            };

            const handleMouseUp = () => {
                if (!resizeState.current || !container) return;
                const finalWidth = container.offsetWidth;
                const path = ReactEditor.findPath(editor, element);
                Transforms.setNodes(editor, { width: finalWidth }, { at: path });
                resizeState.current = null;
                cleanup();
            };

            const cleanup = () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                cleanupRef.current = null;
            };

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            cleanupRef.current = cleanup;
        },
        [editor, element],
    );

    const isActive = selected && focused;

    return (
        <div
            {...attributes}
            className={styles.Embed}
            style={{ display: 'flex', width: '100%', justifyContent: element?.justify || '' }}
            {...element.attr}
        >
            <div contentEditable={false} className={styles.ImageWrapper}>
                <div
                    ref={containerRef}
                    className={`${styles.ImageContainer} ${isActive ? styles.ImageSelected : ''}`}
                    style={{ width: element.width ? `${element.width}px` : undefined }}
                >
                    {loading && (
                        <div className={styles.ImageLoader}>
                            <div className={styles.ImageLoaderSpinner} />
                        </div>
                    )}
                    {resolvedUrl && (
                        <img
                            alt={alt}
                            src={resolvedUrl}
                            className={styles.InlineImage}
                            onDoubleClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openImagePreview(resolvedUrl, alt);
                            }}
                        />
                    )}
                    {isActive &&
                        HANDLE_POSITIONS.map((pos) => (
                            <div
                                key={pos}
                                className={`${styles.ResizeHandle} ${styles[`ResizeHandle_${pos}`]}`}
                                onMouseDown={(e) => handleResizeStart(pos, e)}
                            />
                        ))}
                </div>
            </div>
            {children}
        </div>
    );
};
export default Image;
